import { useEffect, useMemo } from 'react'
import { sanitizeBenefitHtml, markBenefitHtml } from '../utils/benefitHtml'

/**
 * Wide popup showing one VSP plan section's benefits table. The "sections"
 * webhook schema stores each portal section as sanitized HTML (label + table);
 * the slideout lists the plan names and opens this modal for the full table,
 * which is too wide for the slideout panel itself.
 *
 * The markup was already sanitized at ingest by the webhook parser —
 * re-sanitizing here is defense in depth so `dangerouslySetInnerHTML` never
 * trusts Firestore data (allowlisted tags only; no images, URLs, or handlers).
 * The markup is first annotated with our marker classes (spacer rows,
 * bullet glyphs — content/structure detection, never the portal's class
 * names) so the stylesheet can target them; sanitization runs LAST so only
 * DOMPurify output is injected.
 * Closes on overlay click, the × button, or Escape.
 */
export default function VspPlanTableModal({ label, html, onClose }: {
  label: string
  html: string
  onClose: () => void
}) {
  const safeHtml = useMemo(() => sanitizeBenefitHtml(markBenefitHtml(html)), [html])
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="vsp-plan-modal-overlay" onClick={onClose}>
      <div
        className="vsp-plan-modal"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={e => e.stopPropagation()}
      >
        <div className="vsp-plan-modal__header">
          <span className="vsp-plan-modal__title">{label}</span>
          <button className="vsp-plan-modal__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="vsp-plan-modal__body">
          <div className="vsp-plan-table" dangerouslySetInnerHTML={{ __html: safeHtml }} />
        </div>
      </div>
    </div>
  )
}
